/**
 * Discount Service
 *
 * Manages first-month discount offers for Replenishment Center packages.
 * Uses billingPackageCode matching for precise package targeting.
 * Discount codes are configurable via environment variables.
 *
 * @module discountService
 */

import { get } from '@/services/http';
import discountConfig from '@/config/discountConfig';

/**
 * Package configuration from discount API
 */
interface PackageConfig {
  billingPackageId: number;
  interval: 'EVERY_30_DAYS' | 'EVERY_365_DAYS';
}

/**
 * Discount model returned from API
 */
export interface DiscountModel {
  id: number;
  code: string;
  type: 'PERCENTAGE_DISCOUNT' | 'FIXED_DISCOUNT';
  rate: number;
  limitIntervals: number;
  limitUsageNumber: number | null;
  kind: 'REUSE' | 'ONE_TIME';
  merchantId: number | null;
  status: 'ACTIVE' | 'INACTIVE';
  billingPackageCode?: string;     // NEW: Target package by code (e.g., "replenishment_basic")
  package_config?: PackageConfig;  // LEGACY: May exist for old discounts
  createdAt: string;
  updatedAt: string;
}

/**
 * Billing package model
 */
export interface BillingPackageModel {
  id: number;
  name: string;
  code: string;                    // Unique package identifier (e.g., "replenishment_basic")
  price: number;
  yearlyPrice: number;
  monthlyPrice: number;
  features: string[];
  shopifyPlanName: string;
  status: 'ACTIVE' | 'INACTIVE';
}

/**
 * Discount validation response
 */
export interface DiscountValidationResponse {
  valid: boolean;
  discount: DiscountModel | null;
}

/**
 * Get first-month discount code for a Shopify plan
 *
 * Retrieves discount code from configuration (environment variables or defaults).
 * Discount codes can be updated via environment variables without code changes.
 *
 * @param shopifyPlanName - Shopify plan name (e.g., "Basic Shopify", "Shopify Plus")
 * @returns Discount code or null if no discount available
 *
 * @example
 * ```typescript
 * // With VUE_APP_DISCOUNT_CODE_BASIC_SHOPIFY="NEWCODE123"
 * const code = getDiscountCodeForPlan('Basic Shopify');
 * // Returns: 'NEWCODE123'
 * ```
 *
 * @see discountConfig - Configuration loaded from environment variables
 */
export function getDiscountCodeForPlan(shopifyPlanName: string): string | null {
  return discountConfig.getDiscountCode(shopifyPlanName);
}

/**
 * Fetch and validate discount from API
 *
 * @param discountCode - System-generated discount code (e.g., "60X1ED")
 * @returns Validated discount model or null
 */
export async function fetchDiscount(discountCode: string): Promise<DiscountModel | null> {
  try {
    const response: DiscountValidationResponse = await get(
      `/discounts/validate/${discountCode}`
    );

    if (response.valid && response.discount) {
      return response.discount;
    }

    return null;
  } catch (error) {
    console.error('[DiscountService] Error fetching discount:', error);
    return null;
  }
}

/**
 * Get first-month discount for a Shopify plan
 *
 * Combines plan-to-code mapping with API validation
 *
 * @param shopifyPlanName - Shopify plan name
 * @returns Validated discount model or null
 */
export async function getFirstMonthDiscountForPlan(
  shopifyPlanName: string
): Promise<DiscountModel | null> {
  const discountCode = getDiscountCodeForPlan(shopifyPlanName);

  if (!discountCode) {
    return null;
  }

  return fetchDiscount(discountCode);
}

/**
 * Check if discount should be applied to a billing package
 *
 * Uses billingPackageCode matching (preferred) with fallback to legacy billingPackageId
 *
 * @param discount - Discount model from API
 * @param billingPackage - Target billing package
 * @param isYearly - Whether yearly billing is selected
 * @param isExpired - Whether discount timer has expired
 * @returns True if discount should be applied
 */
export function shouldApplyDiscount(
  discount: DiscountModel | null,
  billingPackage: BillingPackageModel,
  isYearly: boolean,
  isExpired: boolean
): boolean {
  // No discount available
  if (!discount) return false;

  // Discount only applies to monthly billing
  if (isYearly) return false;

  // Timer has expired
  if (isExpired) return false;

  // Check discount status
  if (discount.status !== 'ACTIVE') return false;

  // NEW: Check package match by billingPackageCode (preferred)
  if (discount.billingPackageCode) {
    if (discount.billingPackageCode !== billingPackage.code) {
      return false;
    }
  }
  // LEGACY: Fallback to package_config.billingPackageId (backwards compatibility)
  else if (discount.package_config?.billingPackageId) {
    if (discount.package_config.billingPackageId !== billingPackage.id) {
      return false;
    }
  }
  // No matching criteria found
  else {
    return false;
  }

  // Check interval match (if package_config exists)
  if (discount.package_config?.interval && discount.package_config.interval !== 'EVERY_30_DAYS') {
    return false;
  }

  // Check first-month only
  if (discount.limitIntervals !== 1) {
    return false;
  }

  return true;
}

/**
 * Calculate discounted price for a billing package
 *
 * @param originalPrice - Original package price
 * @param discount - Discount model
 * @returns Discounted price (rounded to 2 decimals)
 */
export function calculateDiscountedPrice(
  originalPrice: number,
  discount: DiscountModel | null
): number {
  if (!discount) return originalPrice;

  if (discount.type === 'PERCENTAGE_DISCOUNT') {
    const discountAmount = originalPrice * (discount.rate / 100);
    return Math.round((originalPrice - discountAmount) * 100) / 100;
  }

  if (discount.type === 'FIXED_DISCOUNT') {
    return Math.max(0, Math.round((originalPrice - discount.rate) * 100) / 100);
  }

  return originalPrice;
}

/**
 * Calculate monthly savings from discount
 *
 * @param originalPrice - Original package price
 * @param discountedPrice - Price after discount
 * @returns Monthly savings amount
 */
export function calculateSavings(originalPrice: number, discountedPrice: number): number {
  return Math.round((originalPrice - discountedPrice) * 100) / 100;
}
